import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Card, Table, Tag, Button, Space, Typography, Tooltip, 
    Row, Col, Statistic, Alert, Modal, message, Select, Input,
    Descriptions, Progress, Collapse, Badge, Divider, Dropdown, Menu, Popconfirm
} from 'antd';
import { 
    SyncOutlined, PlayCircleOutlined, PauseCircleOutlined,
    DeleteOutlined, ReloadOutlined, EyeOutlined, ClockCircleOutlined,
    CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined,
    WarningOutlined, SearchOutlined, ClearOutlined, DashboardOutlined,
    DownOutlined, FastForwardOutlined, RedoOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../../services/api';
import { 
    fetchQueueStatus, 
    fetchJobs, 
    pauseQueue, 
    resumeQueue, 
    removeJob,
    cleanQueue,
    retryJob,
    promoteJob,
    resetJobsState
} from '../actions';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const QueueMonitoring = () => {
    const dispatch = useDispatch();
    const { queueStatus, jobs, loading } = useSelector(state => state.adminSettings);
    
    const [selectedQueue, setSelectedQueue] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [jobDetailVisible, setJobDetailVisible] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [refreshInterval, setRefreshInterval] = useState(5000);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [availableQueues, setAvailableQueues] = useState([]); // Dynamic queue list

    // Memoize functions to prevent unnecessary re-renders
    const getQueueBackendName = useCallback((queueDisplayKey) => {
        if (queueDisplayKey.startsWith('bull:')) {
            return queueDisplayKey.replace('bull:', '');
        }
        return queueDisplayKey;
    }, []);

    const getQueueDisplayName = useCallback((queueKey) => {
        const cleanName = queueKey.startsWith('bull:') ? queueKey.replace('bull:', '') : queueKey;
        
        if (cleanName === 'main_worker') {
            return 'Main Worker';
        } else if (cleanName.includes('worker_') && cleanName.endsWith('_worker')) {
            const parts = cleanName.split('_');
            if (parts.length >= 3 && parts[0] === 'worker') {
                const sellerName = parts.slice(1, -1).join('_');
                return `${sellerName} Worker`;
            }
        }
        
        return cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
    }, []);

    const getBackendQueueType = useCallback((selectedQueueId) => {
        if (selectedQueueId === 'all') {
            return 'all';
        }
        
        const queueInfo = availableQueues.find(q => q.id === selectedQueueId);
        if (queueInfo) {
            const originalKey = queueInfo.originalKey;
            return originalKey.startsWith('bull:') ? originalKey.replace('bull:', '') : originalKey;
        }
        
        return selectedQueueId;
    }, [availableQueues]);

    // Memoize available queues to prevent unnecessary updates
    const memoizedAvailableQueues = useMemo(() => {
        if (queueStatus && Object.keys(queueStatus).length > 0) {
            return Object.keys(queueStatus).map(queueKey => {
                const backendQueueName = getQueueBackendName(queueKey);
                const displayName = getQueueDisplayName(queueKey);
                
                return {
                    id: backendQueueName,
                    displayName: displayName,
                    originalKey: queueKey,
                    backendQueueType: backendQueueName,
                    counts: queueStatus[queueKey]?.counts || {}
                };
            });
        }
        return [];
    }, [queueStatus, getQueueBackendName, getQueueDisplayName]);

    // Update available queues only when memoized value changes
    useEffect(() => {
        setAvailableQueues(memoizedAvailableQueues);
    }, [memoizedAvailableQueues]);

    // Memoize refresh functions to prevent recreation on every render
    const handleRefreshStatus = useCallback(async () => {
        await dispatch(fetchQueueStatus());
    }, [dispatch]);

    const handleRefreshJobs = useCallback(async () => {
        dispatch(resetJobsState());
        const backendQueueType = getBackendQueueType(selectedQueue);
        console.log('Fetching jobs for selected queue:', selectedQueue, 'mapped to backend type:', backendQueueType);
        await dispatch(fetchJobs(backendQueueType, selectedStatus));
    }, [dispatch, getBackendQueueType, selectedQueue, selectedStatus]);

    // Auto refresh with proper cleanup
    useEffect(() => {
        if (autoRefresh && refreshInterval > 0) {
            const interval = setInterval(() => {
                handleRefreshStatus();
                handleRefreshJobs();
            }, refreshInterval);
            
            return () => {
                clearInterval(interval);
            };
        }
    }, [autoRefresh, refreshInterval, handleRefreshStatus, handleRefreshJobs]);

    // Initial load - only run once on mount
    useEffect(() => {
        handleRefreshStatus();
        handleRefreshJobs();
    }, []); // Empty dependency array to run only once

    const handlePauseQueue = async (queueName) => {
        try {
            // Use simplified mapping - get backend name directly
            const backendQueueType = getQueueBackendName(queueName);
            
            console.log('Pausing queue:', queueName, 'mapped to:', backendQueueType);
            
            const result = await dispatch(pauseQueue(backendQueueType));
            if (result.success) {
                const displayName = getQueueDisplayName(queueName);
                message.success(`Queue ${displayName} đã được tạm dừng`);
                handleRefreshStatus();
            } else {
                message.error(result.error || 'Không thể tạm dừng queue');
            }
        } catch (error) {
            message.error('Lỗi khi tạm dừng queue');
        }
    };

    const handleResumeQueue = async (queueName) => {
        try {
            // Use simplified mapping - get backend name directly
            const backendQueueType = getQueueBackendName(queueName);
            
            console.log('Resuming queue:', queueName, 'mapped to:', backendQueueType);
            
            const result = await dispatch(resumeQueue(backendQueueType));
            if (result.success) {
                const displayName = getQueueDisplayName(queueName);
                message.success(`Queue ${displayName} đã được khôi phục`);
                handleRefreshStatus();
            } else {
                message.error(result.error || 'Không thể khôi phục queue');
            }
        } catch (error) {
            message.error('Lỗi khi khôi phục queue');
        }
    };

    const handleRemoveJob = async (jobId, jobQueue) => {
        try {
            let backendQueueType = 'main_worker'; // Default fallback
            
            // Use queue from the job record directly (most reliable source)
            if (jobQueue) {
                if (jobQueue === 'main') {
                    backendQueueType = 'main_worker';
                } else {
                    backendQueueType = jobQueue;
                }
            } else if (selectedQueue !== 'all') {
                // Fallback: use the currently selected queue filter
                const selectedQueueInfo = availableQueues.find(q => q.id === selectedQueue);
                backendQueueType = selectedQueueInfo ? selectedQueueInfo.backendQueueType : selectedQueue;
            }
            
            console.log('Removing job:', jobId, 'from queue type:', backendQueueType);
            
            const result = await dispatch(removeJob(backendQueueType, jobId));
            if (result.success) {
                if (result.isRepeatable) {
                    message.success(`Đã xóa lịch lặp lại của job (key: ${result.repeatKey})`);
                } else {
                    message.success('Job đã được xóa');
                }
                handleRefreshJobs();
            } else {
                // Check if it's an active job error
                const errorMsg = result.error || 'Không thể xóa job';
                if (errorMsg.includes('currently being processed') || 
                    errorMsg.includes('active') || 
                    errorMsg.includes('locked')) {
                    // Show warning with option to force delete
                    Modal.warning({
                        title: '⚠️ Job Đang Được Xử Lý',
                        content: (
                            <div>
                                <p>Job này đang được worker xử lý và không thể xóa bằng cách thông thường.</p>
                                <p><strong>Lựa chọn:</strong></p>
                                <ol>
                                    <li>Đợi job hoàn thành (khuyến nghị)</li>
                                    <li>Pause queue rồi thử lại</li>
                                    <li>Force Delete (rủi ro cao - có thể gây lỗi dữ liệu)</li>
                                </ol>
                                <p style={{ color: 'red', marginTop: 10 }}>
                                    Bạn có muốn <strong>FORCE DELETE</strong> job này không? 
                                    Thao tác này có thể gây mất dữ liệu nếu job đang thực sự xử lý.
                                </p>
                            </div>
                        ),
                        okText: 'Force Delete',
                        okType: 'danger',
                        cancelText: 'Hủy',
                        onOk: () => handleForceRemoveJob(jobId, backendQueueType)
                    });
                } else {
                    message.error(errorMsg);
                }
            }
        } catch (error) {
            message.error('Lỗi khi xóa job');
        }
    };

    const handleForceRemoveJob = async (jobId, queueType) => {
        try {
            const response = await api.delete(`/main-worker/queue/job/${jobId}/force`, {
                data: { queueType, confirmForce: true }
            });
            
            const result = response.data;
            
            if (result.success) {
                message.success(`Job ${jobId} đã được force delete (${result.method})`);
                if (result.warning) {
                    message.warning(result.warning, 5);
                }
                handleRefreshJobs();
            } else {
                message.error(result.message || 'Không thể force delete job');
            }
        } catch (error) {
            console.error('Force delete error:', error);
            message.error(error.response?.data?.message || 'Lỗi khi force delete job');
        }
    };

    // Enhanced clean queue function with status parameter
    const handleCleanQueue = async (status, queueName = null) => {
        try {
            // Use queueName directly or selectedQueue
            const targetQueue = queueName || selectedQueue;
            
            let backendQueueType;
            if (targetQueue === 'all') {
                // For 'all', use 'all' as backend type
                backendQueueType = 'all';
            } else if (queueName) {
                // If specific queue name provided, use simplified mapping
                backendQueueType = getQueueBackendName(queueName);
            } else {
                // Use selected queue mapping
                const selectedQueueInfo = availableQueues.find(q => q.id === selectedQueue);
                backendQueueType = selectedQueueInfo ? selectedQueueInfo.backendQueueType : selectedQueue;
            }
            
            console.log('Cleaning queue:', targetQueue, 'mapped to:', backendQueueType, 'status:', status);
            
            const result = await dispatch(cleanQueue(backendQueueType, status));
            if (result.success) {
                const displayName = queueName ? 
                    getQueueDisplayName(queueName) :
                    (availableQueues.find(q => q.id === selectedQueue)?.displayName || selectedQueue);
                
                const statusText = status === 'all' ? 'tất cả jobs' : `các job ${status}`;
                message.success(`Đã dọn dẹp ${statusText} từ ${displayName}`);
                handleRefreshJobs();
                handleRefreshStatus();
            } else {
                message.error(result.error || 'Không thể dọn dẹp queue');
            }
        } catch (error) {
            message.error('Lỗi khi dọn dẹp queue');
        }
    };

    const getJobQueueType = (record) => {
        const currentJobs = jobs || [];
        const targetJob = currentJobs.find(job => job.id === record.id);
        if (targetJob && targetJob.queue) {
            const jobQueueName = targetJob.queue;
            if (jobQueueName === 'main') return 'main_worker';
            const queueInfo = availableQueues.find(q =>
                q.id === jobQueueName ||
                q.displayName.toLowerCase().includes(jobQueueName.toLowerCase())
            );
            return queueInfo ? queueInfo.backendQueueType : jobQueueName;
        }
        if (selectedQueue !== 'all') {
            const selectedQueueInfo = availableQueues.find(q => q.id === selectedQueue);
            return selectedQueueInfo ? selectedQueueInfo.backendQueueType : selectedQueue;
        }
        return 'main_worker';
    };

    const handlePromoteJob = async (record) => {
        try {
            const backendQueueType = getJobQueueType(record);
            const result = await dispatch(promoteJob(backendQueueType, record.id));
            if (result.success) {
                message.success('Job đã được chuyển sang waiting - sẽ chạy ngay');
                if (result.note) {
                    message.info(result.note, 6);
                }
                handleRefreshJobs();
            } else {
                message.error(result.error || 'Không thể promote job');
            }
        } catch (error) {
            message.error('Lỗi khi promote job');
        }
    };

    const handleRetryJob = async (record) => {
        try {
            const backendQueueType = getJobQueueType(record);
            const result = await dispatch(retryJob(backendQueueType, record.id));
            if (result.success) {
                message.success('Job đã được đưa vào hàng chờ retry');
                handleRefreshJobs();
            } else {
                message.error(result.error || 'Không thể retry job');
            }
        } catch (error) {
            message.error('Lỗi khi retry job');
        }
    };

    const handleViewJobDetail = (job) => {
        setSelectedJob(job);
        setJobDetailVisible(true);
    };

    const getStatusColor = (status) => {
        const statusColors = {
            waiting: 'blue',
            active: 'orange',
            completed: 'green',
            failed: 'red',
            delayed: 'purple'
        };
        return statusColors[status] || 'default';
    };

    const getQueueHealthStatus = (queue) => {
        // Check if queue object exists and has basic structure
        if (!queue || typeof queue !== 'object') {
            return { status: 'error', text: 'No Data' };
        }

        // Check if queue is active - handle different possible field names
        const isActive = queue.isActive !== undefined ? queue.isActive : 
                         queue.active !== undefined ? queue.active : 
                         queue.name !== undefined; // If has name, assume it's active

        if (!isActive) {
            return { status: 'error', text: 'Offline' };
        }

        // Check if queue is paused
        if (queue.isPaused === true || queue.paused === true) {
            return { status: 'warning', text: 'Paused' };
        }

        // Check failure rate if we have counts
        if (queue.counts && typeof queue.counts === 'object') {
            const { failed = 0, completed = 0, active = 0, wait = 0 } = queue.counts;
            const totalJobs = failed + completed + active + wait;
            
            // If there are jobs and high failure rate
            if (totalJobs > 0 && failed > 0) {
                const failureRate = failed / totalJobs;
                if (failureRate > 0.1) { // More than 10% failure rate
                    return { status: 'warning', text: 'High Failure Rate' };
                }
            }

            // If there are active jobs, show as processing with blue background
            if (active > 0) {
                return { status: 'processing', text: 'Processing' };
            }

            // If there are waiting jobs, show as active
            if (wait > 0) {
                return { status: 'success', text: 'Active' };
            }
        }

        // Default to healthy if active and no issues
        return { status: 'success', text: 'Healthy' };
    };

    // Updated handler for queue card click with proper mapping
    const handleQueueCardClick = (queueName) => {
        console.log('Clicked queue:', queueName);
        
        // Find the queue in our discovered queues
        const selectedQueueInfo = availableQueues.find(q => q.originalKey === queueName);
        
        if (selectedQueueInfo) {
            console.log('Selected queue info:', selectedQueueInfo);
            
            setSelectedQueue(selectedQueueInfo.id);
            
            // Use the backend queue type for fetching jobs
            dispatch(fetchJobs(selectedQueueInfo.backendQueueType, selectedStatus));
            
            message.info(`Jobs filtered by queue: ${selectedQueueInfo.displayName}`);
        } else {
            console.warn('Queue info not found for:', queueName);
            
            // Fallback: use simplified mapping
            const fallbackQueueId = getQueueBackendName(queueName);
            
            setSelectedQueue(fallbackQueueId);
            dispatch(fetchJobs(fallbackQueueId, selectedStatus));
            
            message.info(`Jobs filtered by queue: ${getQueueDisplayName(queueName)}`);
        }
    };

    // Function to get proper queue display name from job queue value
    const getJobQueueDisplayName = (jobQueueValue) => {
        if (!jobQueueValue) return 'Unknown';
        
        // If it's 'main', show 'Main Worker'
        if (jobQueueValue === 'main') {
            return 'Main Worker';
        }
        
        // If it's a specific worker queue name, try to find it in available queues
        const queueInfo = availableQueues.find(q => 
            q.id === jobQueueValue || 
            q.backendQueueType === jobQueueValue ||
            q.originalKey.includes(jobQueueValue)
        );
        
        if (queueInfo) {
            return queueInfo.displayName;
        }
        
        // Use the simplified display name function
        return getQueueDisplayName(jobQueueValue);
    };

    // Function to get queue color based on queue type
    const getQueueColor = (jobQueueValue) => {
        if (!jobQueueValue) return 'default';
        
        if (jobQueueValue === 'main' || jobQueueValue === 'main_worker') {
            return 'purple';
        }
        
        if (jobQueueValue.includes('worker')) {
            return 'orange';
        }
        
        return 'geekblue';
    };

    // Compact queue card component
    const QueueCard = ({ queueName, queue }) => {
        const health = getQueueHealthStatus(queue);
        const totalJobs = (queue.counts?.waiting || 0) + 
                         (queue.counts?.active || 0) + 
                         (queue.counts?.completed || 0) + 
                         (queue.counts?.failed || 0);

        // Dynamic selection logic based on discovered queues
        const isSelected = (() => {
            if (selectedQueue === 'all') return false;
            
            // Find the queue info
            const queueInfo = availableQueues.find(q => q.originalName === queueName);
            if (queueInfo) {
                return selectedQueue === queueInfo.id;
            }
            
            // Fallback to direct comparison
            return selectedQueue === queueName;
        })();

        // Get background color based on health status
        const getBackgroundColor = (healthStatus) => {
            switch (healthStatus) {
                case 'success':
                    return '#f6ffed'; // Light green for healthy/active
                case 'warning':
                    return '#fffbe6'; // Light yellow for warning/paused
                case 'processing':
                    return '#e6f7ff'; // Light blue for processing
                case 'error':
                default:
                    return '#fff2f0'; // Light red for error/offline
            }
        };

        // Clean dropdown menu items with Popconfirm for dangerous operations
        const getCleanMenuItems = () => {
            // Debug: Log queue structure to understand the data
            console.log('Queue counts structure for menu items:', queue.counts);
            
            // Safely get counts with fallbacks for different field names
            const counts = queue.counts || {};
            const completed = counts.completed || 0;
            const failed = counts.failed || 0;
            // FIX: Handle both 'waiting' and 'wait' field names - prioritize 'wait' as standard
            const waiting = counts.wait || counts.waiting || 0;
            const delayed = counts.delayed || counts.delay || 0; // Handle different field names
            const active = counts.active || 0; // Active jobs count
            
            console.log('Processed counts:', { completed, failed, waiting, delayed, active });
            
            const menuItems = [
                {
                    key: 'completed',
                    label: (
                        <div onClick={(e) => {
                            e.stopPropagation();
                            console.log('Cleanup completed jobs');
                            handleCleanQueue('completed', queueName);
                        }}>
                            <span>Clean Completed</span>
                        </div>
                    ),
                    disabled: completed === 0
                },
                {
                    key: 'failed',
                    label: (
                        <div onClick={(e) => {
                            e.stopPropagation();
                            console.log('Cleanup failed jobs');
                            handleCleanQueue('failed', queueName);
                        }}>
                            <span>Clean Failed</span>
                        </div>
                    ),
                    disabled: failed === 0
                },
                {
                    key: 'wait',
                    label: (
                        <Popconfirm
                            title="Xác nhận dọn dẹp Waiting Jobs"
                            description={`Bạn có chắc chắn muốn dọn dẹp các job waiting từ queue ${getQueueDisplayName(queueName)}?`}
                            onConfirm={(e) => {
                                e?.stopPropagation();
                                console.log('Confirmed cleanup waiting jobs');
                                handleCleanQueue('wait', queueName);
                            }}
                            onCancel={(e) => {
                                e?.stopPropagation();
                                console.log('Cancelled cleanup waiting jobs');
                            }}
                            okText="Có, dọn dẹp"
                            cancelText="Hủy"
                            placement="left"
                            disabled={waiting === 0}
                        >
                            <div onClick={(e) => e.stopPropagation()}>
                                <span>Clean Waiting</span>
                            </div>
                        </Popconfirm>
                    ),
                    disabled: waiting === 0
                },
                {
                    key: 'delayed',
                    label: (
                        <div onClick={(e) => {
                            e.stopPropagation();
                            console.log('Cleanup delayed jobs');
                            handleCleanQueue('delayed', queueName);
                        }}>
                            <span>Clean Delayed</span>
                        </div>
                    ),
                    disabled: delayed === 0
                },
                {
                    key: 'active',
                    label: (
                        <Popconfirm
                            title="⚠️ CẢNH BÁO: Xóa Active Jobs"
                            description={
                                <div>
                                    <p style={{ color: 'red', fontWeight: 'bold', marginBottom: '8px' }}>
                                        Các job ACTIVE đang được xử lý!
                                    </p>
                                    <p>Xóa các job này có thể gây:</p>
                                    <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                                        <li>Mất dữ liệu đang xử lý</li>
                                        <li>Lỗi trong worker</li>
                                        <li>Kết quả không nhất quán</li>
                                    </ul>
                                    <p style={{ fontWeight: 'bold' }}>
                                        Chỉ làm khi thực sự cần thiết!
                                    </p>
                                </div>
                            }
                            onConfirm={(e) => {
                                e?.stopPropagation();
                                console.log('Confirmed cleanup active jobs');
                                handleCleanQueue('active', queueName);
                            }}
                            onCancel={(e) => {
                                e?.stopPropagation();
                                console.log('Cancelled cleanup active jobs');
                            }}
                            okText="Có, xóa active jobs"
                            cancelText="Hủy"
                            okType="danger"
                            placement="left"
                            disabled={active === 0}
                        >
                            <div onClick={(e) => e.stopPropagation()}>
                                <span style={{ color: active > 0 ? '#ff4d4f' : undefined }}>
                                    Clean Active
                                </span>
                            </div>
                        </Popconfirm>
                    ),
                    disabled: active === 0
                },
                {
                    type: 'divider'
                },
                {
                    key: 'all',
                    label: (
                        <Popconfirm
                            title="Xác nhận dọn dẹp TẤT CẢ Jobs"
                            description={`Bạn có chắc chắn muốn dọn dẹp TẤT CẢ jobs từ queue ${getQueueDisplayName(queueName)}? Điều này không thể hoàn tác!`}
                            onConfirm={(e) => {
                                e?.stopPropagation();
                                console.log('Confirmed cleanup all jobs');
                                handleCleanQueue('all', queueName);
                            }}
                            onCancel={(e) => {
                                e?.stopPropagation();
                                console.log('Cancelled cleanup all jobs');
                            }}
                            okText="Có, dọn dẹp tất cả"
                            cancelText="Hủy"
                            okType="danger"
                            placement="left"
                            disabled={totalJobs === 0}
                        >
                            <div onClick={(e) => e.stopPropagation()}>
                                <span style={{ fontWeight: 'bold' }}>Clean All Jobs</span>
                            </div>
                        </Popconfirm>
                    ),
                    disabled: totalJobs === 0
                }
            ];

            return menuItems;
        };

        return (
            <Card 
                size="small" 
                hoverable
                onClick={() => handleQueueCardClick(queueName)}
                style={{ 
                    height: '100%',
                    background: getBackgroundColor(health.status),
                    border: isSelected ? '2px solidrgb(50, 65, 80)' : '1px solid #d9d9d9',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                }}
                bodyStyle={{ padding: '12px' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                        <Text strong style={{ fontSize: '14px', color: isSelected ? '#1890ff' : 'inherit' }}>
                            {queue.name}
                        </Text>
                        <div style={{ marginTop: 2 }}>
                            <Badge 
                                status={
                                    health.status === 'success' ? 'success' : 
                                    health.status === 'warning' ? 'warning' : 
                                    health.status === 'processing' ? 'processing' :
                                    'error'
                                }
                                text={<Text style={{ fontSize: '12px' }}>{health.text}</Text>}
                            />
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Total: {totalJobs}
                        </Text>
                        {isSelected && (
                            <div style={{ marginTop: 2 }}>
                                <Tag color="blue" size="small" style={{ fontSize: '10px' }}>
                                    Selected
                                </Tag>
                            </div>
                        )}
                    </div>
                </div>

                <Row gutter={4} style={{ marginBottom: 8 }}>
                    <Col span={6}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#1890ff', fontSize: '16px', fontWeight: 'bold' }}>
                                {queue.counts?.wait || queue.counts?.waiting || 0}
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>Wait</div>
                        </div>
                    </Col>
                    <Col span={6}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#fa8c16', fontSize: '16px', fontWeight: 'bold' }}>
                                {queue.counts?.active || 0}
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>Active</div>
                        </div>
                    </Col>
                    <Col span={6}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#52c41a', fontSize: '16px', fontWeight: 'bold' }}>
                                {queue.counts?.completed || 0}
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>Done</div>
                        </div>
                    </Col>
                    <Col span={6}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#f5222d', fontSize: '16px', fontWeight: 'bold' }}>
                                {queue.counts?.failed || 0}
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>Failed</div>
                        </div>
                    </Col>
                </Row>

                <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                    {queue.isPaused ? (
                        <Button
                            icon={<PlayCircleOutlined />}
                            type="primary"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleResumeQueue(queueName);
                            }}
                            style={{ fontSize: '12px', height: '24px' }}
                        >
                            Resume
                        </Button>
                    ) : (
                        <Button
                            icon={<PauseCircleOutlined />}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePauseQueue(queueName);
                            }}
                            style={{ fontSize: '12px', height: '24px' }}
                        >
                            Pause
                        </Button>
                    )}
                    <Dropdown
                        menu={{
                            items: getCleanMenuItems()
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={totalJobs === 0}
                    >
                        <Button
                            icon={<ClearOutlined />}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            style={{ fontSize: '12px', height: '24px' }}
                            disabled={totalJobs === 0}
                        >
                            Clean <DownOutlined style={{ fontSize: '10px' }} />
                        </Button>
                    </Dropdown>
                </div>
            </Card>
        );
    };

    const jobColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            render: (id) => <span>{id.slice(-8)}</span>
        },
        {
            title: 'Queue',
            dataIndex: 'queue',
            key: 'queue',
            width: 120,
            render: (queue) => {
                const displayName = getQueueDisplayName(queue);
                const color = getQueueColor(queue);
                
                return (
                    <Tag color={color} style={{ fontSize: '11px', maxWidth: '110px' }} title={displayName}>
                        {displayName.length > 12 ? displayName.substring(0, 12) + '...' : displayName}
                    </Tag>
                );
            }
        },
        {
            title: 'Job Name',
            dataIndex: 'name',
            key: 'name',
            width: 120,
            render: (name) => <Tag color="blue" style={{ fontSize: '11px' }}>{name}</Tag>
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status) => (
                <Tag color={getStatusColor(status)} style={{ fontSize: '11px' }}>
                    {status?.toUpperCase() || 'UNKNOWN'}
                </Tag>
            )
        },
        {
            title: 'Progress',
            dataIndex: 'progress',
            key: 'progress',
            width: 100,
            render: (progress) => (
                <Progress 
                    percent={progress || 0} 
                    size="small" 
                    status={progress === 100 ? 'success' : 'active'}
                    strokeWidth={6}
                />
            )
        },
        {
            title: 'Created',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 120,
            render: (timestamp) => (
                <Text style={{ fontSize: '12px' }}>
                    {timestamp ? new Date(timestamp).toLocaleString('vi-VN', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                    }) : 'N/A'}
                </Text>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="View Details">
                        <Button 
                            icon={<EyeOutlined />} 
                            size="small"
                            type="text"
                            onClick={() => handleViewJobDetail(record)}
                        />
                    </Tooltip>
                    {record.status === 'delayed' && (
                        <Tooltip title="Promote: chạy ngay (chuyển sang waiting)">
                            <Button
                                icon={<FastForwardOutlined />}
                                size="small"
                                type="text"
                                style={{ color: '#722ed1' }}
                                onClick={() => handlePromoteJob(record)}
                            />
                        </Tooltip>
                    )}
                    {record.status === 'failed' && (
                        <Tooltip title="Retry job">
                            <Button
                                icon={<RedoOutlined />}
                                size="small"
                                type="text"
                                style={{ color: '#fa8c16' }}
                                onClick={() => handleRetryJob(record)}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="Remove">
                        <Button 
                            icon={<DeleteOutlined />} 
                            size="small"
                            type="text"
                            danger
                            onClick={() => handleRemoveJob(record.id, record.queue)}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div className="queue-monitoring">
            {/* Compact Queue Status Overview */}
            <Card 
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DashboardOutlined />
                        <span>Queue Status</span>
                        <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                            (Click on a card to filter jobs)
                        </Text>
                    </div>
                }
                extra={
                    <Space size="small">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleRefreshStatus}
                            loading={loading}
                            size="small"
                        >
                            Refresh
                        </Button>
                        <Select
                            value={autoRefresh}
                            onChange={setAutoRefresh}
                            style={{ width: 100 }}
                            size="small"
                        >
                            <Option value={true}>Auto</Option>
                            <Option value={false}>Manual</Option>
                        </Select>
                    </Space>
                }
                style={{ marginBottom: 16 }}
                bodyStyle={{ padding: '12px' }}
            >
                {queueStatus && Object.keys(queueStatus).length > 0 ? (
                    <Row gutter={[12, 12]}>
                        {Object.entries(queueStatus).map(([queueName, queue]) => (
                            <Col xs={24} sm={12} md={8} lg={6} key={queueName}>
                                <QueueCard queueName={queueName} queue={queue} />
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <Alert
                        message="No Queue Services Available"
                        description={
                            <div>
                                <p style={{ margin: '8px 0' }}>Cannot connect to queue services. Please ensure:</p>
                                <ul style={{ margin: '8px 0', paddingLeft: 20, fontSize: '12px' }}>
                                    <li>Main Worker service is running on port 3010</li>
                                    <li>Worker service is running on port 3011</li>
                                    <li>Backend proxy routes are configured correctly</li>
                                    <li>Redis server is running and accessible</li>
                                </ul>
                            </div>
                        }
                        type="error"
                        showIcon
                        action={
                            <Button size="small" onClick={handleRefreshStatus}>
                                Retry Connection
                            </Button>
                        }
                    />
                )}
            </Card>

            {/* Compact Jobs List */}
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ClockCircleOutlined />
                        <span>Jobs Management</span>
                        {selectedQueue !== 'all' && (
                            <Tag color="blue" style={{ fontSize: '11px' }}>
                                Filtered by: {availableQueues.find(q => q.id === selectedQueue)?.displayName || selectedQueue}
                            </Tag>
                        )}
                    </div>
                }
                extra={
                    <Space size="small">
                        <Select
                            value={selectedQueue}
                            onChange={(value) => {
                                console.log('Queue selection changed to:', value);
                                setSelectedQueue(value);
                                
                                // Reset jobs state first before fetching new data
                                dispatch(resetJobsState());
                                
                                // Get proper backend queue type for the selected queue
                                const backendQueueType = getBackendQueueType(value);
                                console.log('Using backend queue type:', backendQueueType, 'for selected queue:', value);
                                
                                dispatch(fetchJobs(backendQueueType, selectedStatus));
                            }}
                            style={{ width: 160 }}
                            size="small"
                            placeholder="Select Queue"
                        >
                            <Option value="all">All Queues</Option>
                            {availableQueues.map(queue => (
                                <Option key={queue.id} value={queue.id}>
                                    {queue.displayName}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            value={selectedStatus}
                            onChange={(value) => {
                                console.log('Status selection changed to:', value);
                                setSelectedStatus(value);
                                
                                // Reset jobs state first before fetching new data
                                dispatch(resetJobsState());
                                
                                // Get proper backend queue type for current selected queue
                                const backendQueueType = getBackendQueueType(selectedQueue);
                                console.log('Using backend queue type:', backendQueueType, 'for status change');
                                
                                dispatch(fetchJobs(backendQueueType, value));
                            }}
                            style={{ width: 100 }}
                            size="small"
                        >
                            <Option value="all">All Status</Option>
                            <Option value="wait">Waiting</Option>
                            <Option value="active">Active</Option>
                            <Option value="completed">Completed</Option>
                            <Option value="failed">Failed</Option>
                            <Option value="delayed">Delayed</Option>
                        </Select>
                        <Button
                            icon={<SearchOutlined />}
                            type="primary"
                            onClick={handleRefreshJobs}
                            loading={loading}
                            size="small"
                        >
                            Load
                        </Button>
                        {selectedQueue !== 'all' && (
                            <Button
                                icon={<ClearOutlined />}
                                onClick={() => {
                                    setSelectedQueue('all');
                                    // Reset jobs state first before fetching new data
                                    dispatch(resetJobsState());
                                    dispatch(fetchJobs('all', selectedStatus));
                                }}
                                size="small"
                                title="Clear queue filter"
                            >
                                Clear Filter
                            </Button>
                        )}
                    </Space>
                }
                bodyStyle={{ padding: '12px' }}
            >
                <Table
                    columns={jobColumns}
                    dataSource={jobs || []}
                    loading={loading}
                    pagination={{ 
                        pageSize: 15,
                        size: 'small',
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `${total} jobs`,
                        pageSizeOptions: ['10', '15', '25', '50']
                    }}
                    scroll={{ x: 700 }}
                    rowKey="id"
                    size="small"
                />
            </Card>

            {/* Job Detail Modal */}
            <Modal
                title={`Job Details: ${selectedJob?.id}`}
                open={jobDetailVisible}
                onCancel={() => setJobDetailVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setJobDetailVisible(false)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {selectedJob && (
                    <Collapse defaultActiveKey={['1']} size="small">
                        <Panel header="Basic Information" key="1">
                            <Descriptions column={2} bordered size="small">
                                <Descriptions.Item label="Job ID">{selectedJob.id}</Descriptions.Item>
                                <Descriptions.Item label="Job Name">{selectedJob.name}</Descriptions.Item>
                                <Descriptions.Item label="Progress">{selectedJob.progress}%</Descriptions.Item>
                                <Descriptions.Item label="Created At">
                                    {selectedJob.timestamp ? new Date(selectedJob.timestamp).toLocaleString() : 'N/A'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Processed At">
                                    {selectedJob.processedOn ? new Date(selectedJob.processedOn).toLocaleString() : 'N/A'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Finished At">
                                    {selectedJob.finishedOn ? new Date(selectedJob.finishedOn).toLocaleString() : 'N/A'}
                                </Descriptions.Item>
                            </Descriptions>
                        </Panel>
                        
                        <Panel header="Job Data" key="2">
                            <pre style={{ 
                                background: '#f6f8fa', 
                                padding: 12, 
                                borderRadius: 6,
                                overflow: 'auto',
                                maxHeight: 300,
                                fontSize: '12px'
                            }}>
                                {JSON.stringify(selectedJob.data, null, 2)}
                            </pre>
                        </Panel>

                        {selectedJob.failedReason && (
                            <Panel header="Error Information" key="3">
                                <Alert
                                    message="Job Failed"
                                    description={selectedJob.failedReason}
                                    type="error"
                                    showIcon
                                    size="small"
                                />
                            </Panel>
                        )}

                        {selectedJob.returnvalue && (
                            <Panel header="Return Value" key="4">
                                <pre style={{ 
                                    background: '#f6f8fa', 
                                    padding: 12, 
                                    borderRadius: 6,
                                    overflow: 'auto',
                                    maxHeight: 300,
                                    fontSize: '12px'
                                }}>
                                    {JSON.stringify(selectedJob.returnvalue, null, 2)}
                                </pre>
                            </Panel>
                        )}
                    </Collapse>
                )}
            </Modal>
        </div>
    );
};

export default QueueMonitoring;